import { useState, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Info, Send } from 'lucide-react';
import {
  ReplySupportTicketRequestSchema,
  SUPPORT_ATTACHMENT_MAX_COUNT,
  type ReplySupportTicketRequest
} from '@bliks-io/contracts';
import { Button, Tooltip, TooltipContent, TooltipTrigger } from '@bliks-io/ui';
import { useReplyToSupportTicket } from '@/api/mutations/useSupport';
import { toastApiError } from '@/api/mutations/toast-api-error';
import { AttachButton } from './AttachButton';
import { AttachmentList } from './AttachmentList';

/**
 * The reply box as one framed composer, like a messenger's: no field label,
 * the text grows with what is typed, the paperclip and send sit inside it.
 * The not-a-live-chat line sits right above it, where the expectation of an
 * instant answer forms.
 */
export function ReplyComposer({
  ticketId,
  reopens,
  onSent
}: {
  ticketId: number;
  /** The ticket is closed: sending the message opens it again. */
  reopens: boolean;
  onSent: () => void;
}) {
  const { t } = useTranslation();
  const mutation = useReplyToSupportTicket(ticketId);
  const [attachments, setAttachments] = useState<File[]>([]);

  const { control, register, handleSubmit, reset } = useForm<ReplySupportTicketRequest>({
    resolver: zodResolver(ReplySupportTicketRequestSchema),
    defaultValues: { body: '' }
  });
  const body = useWatch({ control, name: 'body' });
  const canSend = body.trim().length > 0 && !mutation.isPending;

  const submit = handleSubmit((data) => {
    mutation.mutate(
      { ...data, attachments },
      {
        onSuccess: () => {
          reset();
          setAttachments([]);
          onSent();
        },
        onError: (error) => toastApiError(error, 'supportTickets.detail.replyError')
      }
    );
  });

  const sendOnModifierEnter = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && (event.metaKey || event.ctrlKey) && canSend) {
      event.preventDefault();
      void submit();
    }
  };

  const sendLabel = t(
    reopens ? 'supportTickets.detail.reopenSubmit' : 'supportTickets.detail.replySubmit'
  );

  return (
    <form onSubmit={submit} className="mt-3 space-y-2">
      <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <Info className="h-3.5 w-3.5 shrink-0" />
        {t('supportTickets.detail.notLiveChat')}
      </p>
      <div className="rounded-xl border border-line bg-background focus-within:border-ring">
        <textarea
          {...register('body')}
          aria-label={t('supportTickets.detail.replyLabel')}
          placeholder={t(
            reopens ? 'supportTickets.detail.reopenLabel' : 'supportTickets.detail.replyPlaceholder'
          )}
          onKeyDown={sendOnModifierEnter}
          rows={2}
          className="field-sizing-content block max-h-40 min-h-14 w-full resize-none bg-transparent px-4 pt-3 text-sm outline-none placeholder:text-muted-foreground"
        />
        {attachments.length > 0 && (
          <div className="px-3 pt-2">
            <AttachmentList
              files={attachments}
              onRemove={(index) =>
                setAttachments((current) => current.filter((_, position) => position !== index))
              }
            />
          </div>
        )}
        <div className="flex items-center justify-between px-2 pb-2">
          <AttachButton
            disabled={mutation.isPending || attachments.length >= SUPPORT_ATTACHMENT_MAX_COUNT}
            onAdd={(files) =>
              setAttachments((current) =>
                [...current, ...files].slice(0, SUPPORT_ATTACHMENT_MAX_COUNT)
              )
            }
          />
          <Tooltip>
            <TooltipTrigger asChild>
              <Button type="submit" size="icon-sm" disabled={!canSend} aria-label={sendLabel}>
                <Send />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              {sendLabel} · {t('supportTickets.detail.sendShortcut')}
            </TooltipContent>
          </Tooltip>
        </div>
      </div>
    </form>
  );
}
